/**
 * THE TEXTS WITH ONE HOUSEHOLD, OR ON ONE ENQUIRY, AS A THREAD (ops #26, part 2): in and out in the
 * order they happened, oldest first, newest at the foot where a conversation ends.
 *
 * On a household's page it is every text one of her people sent the business line that landed on her
 * household, and every text ops composed to any of them; on an enquiry it is the texts that landed on
 * it, and nothing goes out to an enquiry. The server reads the newest page of them and says when older
 * ones are kept (`GET /api/messages/thread/...`).
 *
 * A TEXT IN is drawn as `Messages.tsx` draws one, with the same parts, which live here and are shared
 * with it: the words as text and never as markup, what the phone system's scrub took out by kind,
 * the pictures by count, the keyword as its pill, and the phone system's own page for it, where the
 * pictures are. One nobody has handled yet points to the Texts list, which is where it is dealt with.
 * One whose words may hold a stop (ops #30) says so under it: while the hold waits, where its clock
 * stands and the way to the Texts list where it is decided (ops #33); once ended, how and by whom.
 *
 * A TEXT OUT says to whom, what she read, where it stands, and where it did not go, why: the phone
 * system's word as the sentence the visit's page says it in (`visit-detail/shared.ts`), so a code is
 * never what a person reads, and ops's own switch holding it is told apart from the phone system
 * refusing it. Coral where it did not go, because that is a gap somebody closes by ringing her. One
 * the phone system failed because it holds her number says what to do, call her to confirm the
 * number, and on a household's thread offers Number confirmed, which asks the phone system to release
 * it and sends her nothing; then where that release stands, and the button again where a new press
 * asks again (ops #33).
 *
 * A FORGOTTEN PERSON'S TEXT is what the forget left of it: when it came, and no words, no number and
 * no name the forget blanked, because none of them is in what the server sends.
 *
 * ON A HOUSEHOLD, THE OFFICE WRITES (ops #32): "Write a text" at the foot, to one of her people with a
 * number, and "Reply" on each text of theirs from the number its sender still has, the composer
 * opening under it with her words quoted (`Composer.tsx`). A text out that answers a text in on
 * the page says which. A text written reads the thread again, so it shows here as the door left it.
 *
 * NOTHING IS TEXTED FROM AN ENQUIRY. There is no box to type in and no Reply: somebody who texted
 * first is answered by a call or an email (the owner, 2026-09-23), and a reply goes to a booked client
 * whose website box grant stands, and to nobody else (the owner, ops #32, 2026-10-16).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  confirmNumber,
  getTextThread,
  type ReleaseView,
  type StopHoldView,
  type TextThreadPage,
  type ThreadTextIn,
  type ThreadTextOut,
} from '../api.ts';
import { MEASURE, Pill } from '../components/AppShell.tsx';
import { Button } from '../components/Field.tsx';
import { Busy, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { formatDay, formatInstant, formatPhone } from '../format.ts';
import { BUSINESS_DAYS_WORDS, HOLD_PILL_WORDS, holdClockWords, holdEndedWords } from '../holds.ts';
import {
  INBOUND_KEYWORD_LABELS,
  INBOUND_MESSAGE_STATE_LABELS,
  MESSAGE_INTENT_STATE_LABELS,
  STOP_HOLD_OUTCOME_LABELS,
} from '../labels.ts';
import { TextComposer } from './Composer.tsx';
import { heldUntil, whatToDoAbout, whyItDidNotGo } from './visit-detail/shared.ts';

/** Whose thread: one household, or one enquiry. */
export type ThreadOf = { readonly kind: 'client' | 'enquiry'; readonly id: string };

/** One of the household's people, of whom those with a number may be written to. */
export interface ThreadPerson {
  readonly id: string;
  readonly displayName: string;
  readonly phoneE164: string | null;
}

/**
 * The kinds the phone system's scrub removes, as somebody would say them. The phone system writes
 * each with hyphens; they are looked up here with underscores, the way `ACCESS_SECRET_KINDS` spells
 * the same idea, because the house-style scanner reads a hyphenated word as a word of its own and
 * one of the six would read as a column this product refuses (binding rule 4). A kind this list
 * does not know yet is shown as the phone system wrote it, with spaces for its hyphens, rather than
 * dropped. A map rather than an object, so a kind that happens to name something every object has
 * is a kind it does not know and nothing else.
 */
const REMOVED_WORDS: ReadonlyMap<string, string> = new Map([
  ['payment_card', 'a card number'],
  ['government_id', 'an identity number'],
  ['bank_account', 'a bank account number'],
  ['routing_number', 'a routing number'],
  ['access_code', 'an access code'],
  ['key_location', 'where a key is kept'],
]);

export function removedWords(kinds: readonly string[]): string {
  return kinds
    .map((kind) => REMOVED_WORDS.get(kind.replace(/-/g, '_')) ?? kind.replace(/-/g, ' '))
    .join(', ');
}

export function photoWords(count: number): string {
  return count === 1 ? '1 photo' : `${count} photos`;
}

/** A page of the phone system's, and only ever one: anything else is not drawn as a link. */
export function isPhonePage(url: string | null): url is string {
  return url !== null && url.startsWith('https://');
}

/**
 * WHAT SHE WROTE, AS TEXT. A child of the paragraph and nothing else: React escapes it, so markup is
 * characters. Never `dangerouslySetInnerHTML`, and never cut short.
 */
export function TextBody({ body }: { body: string | null }) {
  if (body === null || body === '') {
    return <p className="mt-2 text-sm text-navy/60">No words to show.</p>;
  }
  return <p className="mt-2 text-base whitespace-pre-wrap wrap-anywhere">{body}</p>;
}

export function TextThread({
  of,
  herName = null,
  people = [],
}: {
  of: ThreadOf;
  /** Who a text with no person on it is from: the enquiry's own name. */
  herName?: string | null;
  /** The household's people, of whom those with a number may be written to. None on an enquiry. */
  people?: readonly ThreadPerson[];
}) {
  const [thread, setThread] = useState<TextThreadPage | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The text of hers being answered, or 'new' for a text that answers none; one at a time.
  const [composing, setComposing] = useState<ThreadTextIn | 'new' | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const { kind, id } = of;
  const reachable = kind === 'client' ? people.filter((person) => person.phoneE164 !== null) : [];

  const compose = (what: ThreadTextIn | 'new') => {
    setSaid(null);
    setComposing(what);
  };
  const sent = (sentence: string) => {
    setComposing(null);
    setSaid(sentence);
    setAttempt((n) => n + 1);
  };
  /**
   * Only a household's text, from one of her people still on it, from the number that person has
   * now: the door answers no other (`not_her_text`), so a text the forget blanked, one from a number
   * since changed or taken off, and one from somebody since removed are answered by a new text.
   */
  const answerable = (text: ThreadTextIn) =>
    kind === 'client' &&
    text.contactId !== null &&
    text.fromE164 !== null &&
    people.some((person) => person.id === text.contactId && person.phoneE164 === text.fromE164);

  useEffect(() => {
    let live = true;
    setError(null);
    setThread(null);
    getTextThread({ kind, id })
      .then((answer) => {
        if (live) setThread(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The texts did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [kind, id, attempt]);

  if (error) {
    return (
      <ErrorNotice
        title="The texts did not load"
        message={error.message}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    );
  }
  if (thread === null) return <Busy what="the texts" />;

  /** The text in on this page a text out answers, by the phone system's id, if it is here. */
  const answered = (text: ThreadTextOut): ThreadTextIn | undefined =>
    text.inReplyTo
      ? thread.texts.find(
          (other): other is ThreadTextIn =>
            other.direction === 'in' && other.phoneMessageId === text.inReplyTo,
        )
      : undefined;

  return (
    <div className="flex flex-col gap-2">
      {said ? <SavedNotice>{said}</SavedNotice> : null}
      {thread.texts.length === 0 ? (
        <p className={`text-sm text-navy/70 ${MEASURE}`}>
          {kind === 'client'
            ? 'No texts with this household yet.'
            : 'No texts on this enquiry yet.'}
        </p>
      ) : (
        <>
          {thread.more ? (
            <p className={`text-xs text-navy/60 ${MEASURE}`}>
              The newest {thread.limit} are shown. The older ones are still kept.
            </p>
          ) : null}
          <ol aria-label="Texts" className="flex flex-col gap-2">
            {thread.texts.map((text) =>
              text.direction === 'in' ? (
                <li key={`in-${text.id}`} data-direction="in" className="min-w-0">
                  <div className="mr-6 min-w-0">
                    <TextIn
                      text={text}
                      herName={herName}
                      onReply={answerable(text) ? () => compose(text) : undefined}
                    />
                  </div>
                  {composing !== null && composing !== 'new' && composing.id === text.id ? (
                    <TextComposer
                      people={[{ id: text.contactId!, displayName: text.contactName ?? 'them' }]}
                      answering={{ id: text.id, contactId: text.contactId!, body: text.body }}
                      onSent={sent}
                      onCancel={() => setComposing(null)}
                    />
                  ) : null}
                </li>
              ) : (
                <li key={`out-${text.id}`} data-direction="out" className="ml-6 min-w-0">
                  <TextOut
                    text={text}
                    answers={answered(text)}
                    householdId={kind === 'client' ? id : null}
                    onChanged={() => setAttempt((n) => n + 1)}
                  />
                </li>
              ),
            )}
          </ol>
        </>
      )}
      {kind === 'enquiry' ? (
        <p className={`text-xs text-navy/60 ${MEASURE}`}>
          Nothing is texted from here. Somebody who texted first is answered by a call or an email.
        </p>
      ) : composing === 'new' ? (
        <TextComposer people={reachable} onSent={sent} onCancel={() => setComposing(null)} />
      ) : reachable.length > 0 ? (
        <div>
          <Button tone="quiet" onClick={() => compose('new')}>
            Write a text
          </Button>
        </div>
      ) : (
        <p className={`text-xs text-navy/60 ${MEASURE}`}>
          Nobody on this household has a number on file, so nothing can be texted from here.
        </p>
      )}
    </div>
  );
}

/** Who sent a text in: her person on the household, else the enquiry's name, else her number. */
function senderOf(text: ThreadTextIn, herName: string | null): string {
  return text.contactName ?? herName ?? formatPhone(text.fromE164) ?? 'A sender with no number';
}

/** One text she sent, on the left, with Reply where the office may answer it from here. */
function TextIn({
  text,
  herName,
  onReply,
}: {
  text: ThreadTextIn;
  herName: string | null;
  onReply?: (() => void) | undefined;
}) {
  return (
    <div className="rounded-lg border border-navy/12 bg-parchment p-3">
      <Heading who={senderOf(text, herName)} at={text.at} />
      <TextBody body={text.body} />
      {text.redacted.length > 0 ? (
        <p className="mt-1 text-xs text-navy/65">
          Taken out before it reached us: {removedWords(text.redacted)}.
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-2">
        {text.keyword ? <Pill tone="gold">{INBOUND_KEYWORD_LABELS[text.keyword]}</Pill> : null}
        {text.mediaCount > 0 ? <Pill>{photoWords(text.mediaCount)}</Pill> : null}
        <Pill>{INBOUND_MESSAGE_STATE_LABELS[text.handled]}</Pill>
      </div>
      {text.hold ? <HoldLine hold={text.hold} /> : null}
      {text.handled === 'open' || isPhonePage(text.phonePage) ? (
        <ul className="mt-1 flex flex-col text-sm">
          {text.handled === 'open' ? (
            <li>
              <Link
                to="/messages"
                className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
              >
                Deal with it on the Texts list
              </Link>
            </li>
          ) : null}
          {isPhonePage(text.phonePage) ? (
            <li>
              <a
                href={text.phonePage}
                target="_blank"
                rel="noreferrer"
                className="flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
              >
                See it in the phone system
              </a>
            </li>
          ) : null}
        </ul>
      ) : null}
      {onReply ? (
        <div className="mt-2">
          <Button tone="quiet" onClick={onReply}>
            Reply
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * THE STOP HER WORDS MAY HOLD (ops #30), under her text: while it waits, the gold pill, where its clock
 * stands and the way to the Texts list, where it is decided (ops #33); once ended, how, by whom and
 * when. Never the number, which the server does not send.
 */
function HoldLine({ hold }: { hold: StopHoldView }) {
  const ended = holdEndedWords(hold);
  if (hold.outcome === null || ended === null) {
    return (
      <div className="mt-2 border-t border-navy/10 pt-2" data-hold={hold.id}>
        <div className="flex flex-wrap gap-2">
          <Pill tone="gold">{HOLD_PILL_WORDS}</Pill>
        </div>
        <p className={`mt-1 text-sm ${MEASURE}`}>{holdClockWords(hold)}</p>
        <p className="mt-1 text-xs text-navy/65">{BUSINESS_DAYS_WORDS}</p>
        <Link
          to="/messages"
          className="flex min-h-11 desk:min-h-9 items-center text-sm underline underline-offset-2"
        >
          Decide it on the Texts list
        </Link>
      </div>
    );
  }
  return (
    <div className="mt-2 border-t border-navy/10 pt-2" data-hold={hold.id}>
      <div className="flex flex-wrap gap-2">
        <Pill>{STOP_HOLD_OUTCOME_LABELS[hold.outcome]}</Pill>
      </div>
      <p className={`mt-1 text-sm wrap-anywhere ${MEASURE}`}>{ended}</p>
    </div>
  );
}

/** States in which a text out did not go and will not: the gap somebody closes by ringing her. */
const DID_NOT_GO: ReadonlySet<ThreadTextOut['state']> = new Set(['failed', 'refused_by_consent']);

/**
 * One text ops composed, on the right, with where it stands and why it did not go, the text of hers
 * it answers where that is on the page, and, for one the phone system failed because it holds her
 * number, what to do and where its release stands (ops #33).
 */
function TextOut({
  text,
  answers,
  householdId,
  onChanged,
}: {
  text: ThreadTextOut;
  /** The text of hers it answers, where that is on the page (ops #32). */
  answers: ThreadTextIn | undefined;
  /** The household whose thread this is, which Number confirmed is pressed on; null on an enquiry. */
  householdId: string | null;
  /** Read the thread again, once a press landed. */
  onChanged: () => void;
}) {
  const why = whyItDidNotGo(text);
  const todo = whatToDoAbout(text);
  const hold = heldUntil(text, Date.now());
  const missed = DID_NOT_GO.has(text.state);
  return (
    <div
      className={`rounded-lg border bg-navy/5 p-3 ${missed ? 'border-coral/50' : 'border-navy/12'}`}
    >
      <Heading who={`To ${text.contactName ?? 'them'}`} at={text.at} />
      {answers ? (
        <p className="mt-1 text-xs text-navy/65">
          Answering their text of {formatInstant(answers.at) ?? answers.at}
        </p>
      ) : null}
      <TextBody body={text.body} />
      <div className="mt-2 flex flex-wrap gap-2">
        <Pill tone={missed ? 'coral' : text.state === 'delivered' ? 'sage' : 'quiet'}>
          {MESSAGE_INTENT_STATE_LABELS[text.state]}
        </Pill>
      </div>
      {why ? <p className={`mt-2 text-sm ${MEASURE}`}>{why}</p> : null}
      {todo ? <p className={`mt-1 text-sm font-medium ${MEASURE}`}>{todo}</p> : null}
      {todo && householdId ? (
        <ReleaseLine text={text} householdId={householdId} onChanged={onChanged} />
      ) : null}
      {text.state === 'refused_by_consent' ? (
        <p className={`mt-2 text-sm ${MEASURE}`}>
          They have not agreed to texts, so it was never sent.
        </p>
      ) : null}
      {hold ? (
        <p className="mt-2 text-sm">
          Held for the phone system's texting hours until {formatInstant(hold)}.
        </p>
      ) : null}
    </div>
  );
}

/** One sentence, ending as a sentence ends, so a reason the server wrote never runs into the next. */
function asSentence(words: string): string {
  const last = words.charAt(words.length - 1);
  return last === '.' || last === '!' || last === '?' ? words : `${words}.`;
}

/**
 * WHERE A RELEASE STANDS, in words: who confirmed the number and on which day, then what the phone
 * system did with it, or why it never went. Coral where her STOP stood there, because that is a gap
 * somebody closes by ringing her.
 */
function releaseWords(release: ReleaseView): { readonly words: string; readonly coral: boolean } {
  const who = `Number confirmed by ${release.askedByName ?? 'somebody in the office'} on ${formatDay(release.askedOn) ?? release.askedOn}`;
  switch (release.state) {
    case 'asked':
      return { words: `${who}; waiting for the phone system.`, coral: false };
    case 'released': {
      const when =
        release.answeredOn === null || release.answeredOn === release.askedOn
          ? 'the same day'
          : `on ${formatDay(release.answeredOn) ?? release.answeredOn}`;
      return {
        words: `${who}, and the phone system released it ${when}. The next text brings the opt-in confirmation ahead of it.`,
        coral: false,
      };
    }
    case 'refused_stop':
      return {
        words: `${who}, but the phone system says their number texted STOP, which had not reached ops when the release went. No text can go until they text START to the business number: call them.`,
        coral: true,
      };
    case 'parked':
      return {
        words: `${who}, but it did not go: ${asSentence(release.error ?? 'nobody said why')} A new press asks again.`,
        coral: false,
      };
    case 'unclear':
      return {
        words: `${who}; the phone system took it, but its answer did not say whether it released the number.`,
        coral: false,
      };
  }
}

/**
 * NUMBER CONFIRMED, under a text the phone system failed because it holds her number (ops #33): where
 * the newest release of it stands, and the button while nobody has pressed it or a new press asks
 * again. The button opens one question first, because pressing it tells the phone system the number
 * is right; nothing is sent to her by it. The server decides whether it may go, and its refusal is
 * shown in its own words; a press that landed reads the thread again.
 */
function ReleaseLine({
  text,
  householdId,
  onChanged,
}: {
  text: ThreadTextOut;
  householdId: string;
  onChanged: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const release = text.release ?? null;
  const said = release ? releaseWords(release) : null;
  const offered = release === null || release.mayAskAgain;

  async function confirm() {
    setBusy(true);
    setProblem(null);
    try {
      await confirmNumber(householdId, text.contactId, text.id);
      setAsking(false);
      onChanged();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2">
      {said ? (
        <p
          data-release={release?.state}
          className={`text-sm wrap-anywhere ${MEASURE} ${said.coral ? 'text-coral' : ''}`}
        >
          {said.words}
        </p>
      ) : null}
      {offered && !asking ? (
        <div className="mt-2">
          <Button tone="quiet" onClick={() => setAsking(true)}>
            Number confirmed
          </Button>
        </div>
      ) : null}
      {asking ? (
        <div className="mt-2 rounded-lg border border-navy/20 p-3">
          <p className={`mb-3 text-sm ${MEASURE}`}>
            Did you call them and confirm the number is right? The phone system is told to release
            it, and the next text brings the opt-in confirmation ahead of it. Nothing is sent by
            pressing this.
          </p>
          {problem ? (
            <div className="mb-3">
              <ErrorNotice title="Not written down" message={problem} />
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void confirm()} disabled={busy}>
              {busy ? 'Writing it down' : 'Yes, the number is right'}
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setAsking(false);
                setProblem(null);
              }}
              disabled={busy}
            >
              Not yet
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Who and when, on one line that wraps at a phone's width. */
function Heading({ who, at }: { who: string; at: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
      <p className="min-w-0 text-sm font-medium wrap-anywhere">{who}</p>
      <p className="text-xs text-navy/65">
        <time dateTime={at}>{formatInstant(at) ?? at}</time>
      </p>
    </div>
  );
}
